import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, Pencil, Truck, Search, Trash2, FileText, DollarSign, Calendar } from 'lucide-react';
import { DatePickerField } from '../components/DatePickerField';
import { format } from 'date-fns';

export default function Suppliers() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    phone: '',
    location: '',
    notes: ''
  });

  // Payment Modal State
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierPayments, setSupplierPayments] = useState([]);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [paymentData, setPaymentData] = useState({
    amount: '',
    method: 'Cash',
    transaction_code: '',
    remarks: ''
  });

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/suppliers');
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const fetchSupplierPayments = async (supplierId) => {
    try {
      const response = await api.get(`/suppliers/${supplierId}/payments`);
      setSupplierPayments(response.data);
    } catch (error) {
      toast.error('Failed to load payments');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Supplier name is required');
      return;
    }

    try {
      if (editingSupplier) {
        await api.put(`/suppliers/${editingSupplier.id}`, formData);
        toast.success('Supplier updated');
      } else {
        await api.post('/suppliers', formData);
        toast.success('Supplier created');
      }
      setDialogOpen(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', contact: '', phone: '', location: '', notes: '' });
    setEditingSupplier(null);
  };

  const openEditDialog = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      contact: supplier.contact || '',
      phone: supplier.phone || '',
      location: supplier.location || '',
      notes: supplier.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this supplier?')) return;
    try {
      await api.delete(`/suppliers/${id}`);
      toast.success('Supplier deleted');
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  // Payment handlers
  const openPaymentDialog = async (supplier) => {
    setSelectedSupplier(supplier);
    setEditingPayment(null);
    resetPaymentForm();
    await fetchSupplierPayments(supplier.id);
    setPaymentDialogOpen(true);
  };

  const resetPaymentForm = () => {
    setPaymentData({ amount: '', method: 'Cash', transaction_code: '', remarks: '' });
    setPaymentDate(new Date());
    setEditingPayment(null);
  };

  const openEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentDate(new Date(payment.date));
    setPaymentData({
      amount: payment.amount.toString(),
      method: payment.method || 'Cash',
      transaction_code: payment.transaction_code || '',
      remarks: payment.remarks || ''
    });
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const payload = {
      date: format(paymentDate, 'yyyy-MM-dd'),
      amount: parseFloat(paymentData.amount),
      method: paymentData.method,
      transaction_code: paymentData.transaction_code || null,
      remarks: paymentData.remarks || null
    };

    try {
      if (editingPayment) {
        await api.put(`/suppliers/${selectedSupplier.id}/payments/${editingPayment.id}`, payload);
        toast.success('Payment updated');
      } else {
        await api.post(`/suppliers/${selectedSupplier.id}/payments`, payload);
        toast.success('Payment recorded');
      }
      resetPaymentForm();
      await fetchSupplierPayments(selectedSupplier.id);
      fetchSuppliers(); // Refresh totals
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payment');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await api.delete(`/suppliers/${selectedSupplier.id}/payments/${paymentId}`);
      toast.success('Payment deleted');
      await fetchSupplierPayments(selectedSupplier.id);
      fetchSuppliers(); // Refresh totals
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete payment');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // Calculate supplier balance from statement data
  const getSupplierBalance = (supplier) => {
    // For now use outstanding_balance from the supplier record
    // This will be recalculated when viewing statement
    return supplier.total_cost - (supplierPayments.reduce((sum, p) => sum + p.amount, 0) || supplier.total_paid || 0);
  };

  const filteredSuppliers = suppliers.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#1B4D3E]">Suppliers</h1>
          <p className="text-slate-500 mt-1">Manage your maize suppliers</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary gap-2 self-start" data-testid="add-supplier-btn">
              <Plus className="h-4 w-4" />
              Add Supplier
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Supplier name"
                  data-testid="supplier-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact">Contact Person</Label>
                  <Input
                    id="contact"
                    value={formData.contact}
                    onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                    placeholder="Contact name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Phone number"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Location"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" data-testid="supplier-submit-btn">
                  {editingSupplier ? 'Update' : 'Create'} Supplier
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search suppliers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="supplier-search"
        />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Truck className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Suppliers</p>
                <p className="text-2xl font-bold text-slate-900">{suppliers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Truck className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Supplied</p>
                <p className="text-2xl font-bold text-slate-900">
                  {new Intl.NumberFormat('en-KE').format(suppliers.reduce((sum, s) => sum + (s.total_supplied_kg || 0), 0))} kg
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Truck className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Outstanding Balance</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(suppliers.reduce((sum, s) => sum + (s.outstanding_balance || 0), 0))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead className="text-right">Supplied (kg)</TableHead>
                  <TableHead className="text-right">Total Cost</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSuppliers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                      No suppliers found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSuppliers.map((supplier) => (
                    <TableRow key={supplier.id} className="table-row" data-testid={`supplier-row-${supplier.id}`}>
                      <TableCell className="font-medium">{supplier.name}</TableCell>
                      <TableCell>{supplier.contact || '-'}</TableCell>
                      <TableCell>{supplier.phone || '-'}</TableCell>
                      <TableCell>{supplier.location || '-'}</TableCell>
                      <TableCell className="text-right">{new Intl.NumberFormat('en-KE').format(supplier.total_supplied_kg || 0)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(supplier.total_cost)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(supplier.total_paid)}</TableCell>
                      <TableCell className="text-right font-medium text-red-600">
                        {formatCurrency(supplier.outstanding_balance)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openPaymentDialog(supplier)}
                            title="Record Payment"
                            className="text-green-600 border-green-200 hover:bg-green-50"
                            data-testid={`record-payment-supplier-${supplier.id}`}
                          >
                            <DollarSign className="h-4 w-4 mr-1" />
                            Pay
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/suppliers/${supplier.id}/statement`)}
                            title="View Statement"
                            data-testid={`statement-supplier-${supplier.id}`}
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(supplier)}
                            data-testid={`edit-supplier-${supplier.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(supplier.id)}
                              data-testid={`delete-supplier-${supplier.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={(open) => { setPaymentDialogOpen(open); if (!open) resetPaymentForm(); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Record Payment - {selectedSupplier?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedSupplier && (
            <div className="space-y-4 overflow-y-auto flex-1">
              {/* Supplier Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-slate-50">
                  <CardContent className="p-3">
                    <p className="text-xs text-slate-500">Total Cost</p>
                    <p className="text-lg font-bold text-slate-900">{formatCurrency(selectedSupplier.total_cost)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50">
                  <CardContent className="p-3">
                    <p className="text-xs text-green-600">Total Paid</p>
                    <p className="text-lg font-bold text-green-700">{formatCurrency(selectedSupplier.total_paid)}</p>
                  </CardContent>
                </Card>
                <Card className="bg-red-50">
                  <CardContent className="p-3">
                    <p className="text-xs text-red-600">Balance</p>
                    <p className="text-lg font-bold text-red-700">{formatCurrency(selectedSupplier.outstanding_balance)}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Payment Form */}
              <form onSubmit={handlePaymentSubmit} className="space-y-4 p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold text-slate-900">{editingPayment ? 'Edit Payment' : 'New Payment'}</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <DatePickerField date={paymentDate} onDateChange={setPaymentDate} />
                  </div>
                  <div className="space-y-2">
                    <Label>Amount (KES) *</Label>
                    <Input
                      type="number"
                      value={paymentData.amount}
                      onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                      placeholder="0"
                      data-testid="supplier-payment-amount"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Method</Label>
                    <Select value={paymentData.method} onValueChange={(v) => setPaymentData({ ...paymentData, method: v })}>
                      <SelectTrigger data-testid="supplier-payment-method">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Cash">Cash</SelectItem>
                        <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                        <SelectItem value="Mobile Money (M-Pesa)">Mobile Money (M-Pesa)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Transaction Code</Label>
                    <Input
                      value={paymentData.transaction_code}
                      onChange={(e) => setPaymentData({ ...paymentData, transaction_code: e.target.value })}
                      placeholder="e.g., M-Pesa code"
                      data-testid="supplier-payment-code"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Remarks</Label>
                  <Textarea
                    value={paymentData.remarks}
                    onChange={(e) => setPaymentData({ ...paymentData, remarks: e.target.value })}
                    placeholder="Optional notes"
                    rows={2}
                    data-testid="supplier-payment-remarks"
                  />
                </div>
                <div className="flex gap-2">
                  {editingPayment && (
                    <Button type="button" variant="outline" onClick={resetPaymentForm}>
                      Cancel Edit
                    </Button>
                  )}
                  <Button type="submit" className="btn-primary flex-1" data-testid="supplier-payment-submit">
                    {editingPayment ? 'Update Payment' : 'Record Payment'}
                  </Button>
                </div>
              </form>

              {/* Payment History */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Payment History</h4>
                {supplierPayments.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4 bg-slate-50 rounded-lg">
                    No payments recorded yet
                  </p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead>T. Code</TableHead>
                          <TableHead>Remarks</TableHead>
                          <TableHead className="text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {supplierPayments.map((payment) => (
                          <TableRow key={payment.id} data-testid={`payment-row-${payment.id}`}>
                            <TableCell className="whitespace-nowrap">{payment.date}</TableCell>
                            <TableCell className="text-right font-medium text-green-600 whitespace-nowrap">
                              {formatCurrency(payment.amount)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">{payment.method || '-'}</TableCell>
                            <TableCell className="font-mono text-xs">{payment.transaction_code || '-'}</TableCell>
                            <TableCell className="text-slate-500 max-w-[150px] truncate">{payment.remarks || '-'}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditPayment(payment)}
                                  data-testid={`edit-payment-${payment.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeletePayment(payment.id)}
                                  data-testid={`delete-payment-${payment.id}`}
                                >
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
